import ajax from "plugins/ajax";
import i18n from "plugins/i18n";
import Vue from "vue";

const nbNotePerPage = 20;

const store = {
    namespaced: true,
    state: {
        pending: {
            create_note: false,
            edit_note: false,
            reload_notes: false,
            load_notes: false,
            last_note_updated: false,
            load_single_note: false,
            delete_note: false
        },
        selectedNote: {
            title: "_(no title)_",
            content: ""
        },
        lastSelectedUpdate: null,
        notes: []
    },
    getters: {
        /**
         * Get the current page searched at the moment.
         *
         * @param {State} state The Vuex state.
         */
        getCurrentPage: function(state) {
            if (!Array.isArray(state.notes) || state.notes.length === 0) {
                return 1;
            }
            const quotient = Math.floor(state.notes.length / nbNotePerPage);
            const modulo = state.notes.length % nbNotePerPage;

            return (modulo !== 0) ? quotient + 1 : quotient;
        }
    },
    actions: {
        /**
         * Create a new note.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} note The data.
         * @return {Object} Ajax response object.
         */
        createNote: function(store, note) {
            return ajax(store, "post", "/notes", note, "CREATE_NOTE", false, {
                type: "success",
                message: i18n.t("store.message.create-note-success"),
                onlyCurrentRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Edit an existing note.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} note The data.
         * @return {Object} Ajax response object.
         */
        editNote: function(store, note) {
            return ajax(store, "put", "/notes/" + note.id, note, "EDIT_NOTE", "EDIT_NOTE" + note.id, {
                type: "success",
                message: i18n.t("store.message.edit-note-success"),
                onlyCurrentRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Get latest time the given note was updated.
         *
         * @param {Store} store The Vuex store.
         * @param {Object} note The data.
         * @return {Object} Ajax response object.
         */
        lastNoteUpdated: function(store, note) {
            return ajax(store, "get", "/notes/" + note.id + "/last-update", null, "LAST_NOTE_UPDATED", "LAST_NOTE_UPDATED" + note.id, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Reload notes.
         *
         * @param {Store} store The Vuex store.
         * @param {String} search The searched terms.
         * @return {Object} Ajax response object.
         */
        reloadNotes: function(store, search) {
            if (typeof search === "string") {
                store.commit("search/setSearch", search, {
                    root: true
                });
            }
            const params = ["per_page=" + nbNotePerPage];

            if (store.rootState.search.search) {
                params.push("search=" + encodeURIComponent(store.rootState.search.search));
            }

            let query = (params.length > 0) ? "?" + params.join("&") : "";

            return ajax(store, "get", "/notes" + query, null, "RELOAD_NOTES", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            }).then(function(response) {
                const data = response.data;
                store.rootState.search.suggestions.splice(0,
                    store.rootState.search.suggestions.length);
                if (data.type === "suggestion") {
                    for (let i = 0, l = data.content.length; i < l; i++) {
                        store.rootState.search.suggestions.push(data.content[i]);
                    }
                }
                return response;
            });
        },

        /**
         * Load a bunch of notes.
         *
         * @param {Store} store The Vuex store.
         * @return {Object} Ajax response object.
         */
        loadNotes: function(store) {
            const page = store.getters.getCurrentPage;
            const params = [
                "per_page=" + nbNotePerPage,
                "page=" + encodeURIComponent(page + 1)
            ];

            if (store.rootState.search.search) {
                params.push("search=" + store.rootState.search.search);
            }

            // Adding search after parameters to params list
            let query = (params.length > 0) ? "?" + params.join("&") : "";

            return ajax(store, "get", "/notes" + query, null, "LOAD_NOTES", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            }).then(function(response) {
                store.rootState.search.suggestions.splice(0,
                    store.rootState.search.suggestions.length);
                return response;
            });
        },

        /**
         * Load a note.
         *
         * @param {Store} store The Vuex store.
         * @param {Number} id The note id to load.
         * @return {Object} Ajax response object.
         */
        loadSingleNote: function(store, id) {
            return ajax(store, "get", "/notes/" + id, null, "LOAD_SINGLE_NOTE", false, null, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        },

        /**
         * Delete a note.
         *
         * @param {Store} store The Vuex store.
         * @param {Number} id The note id to delete.
         * @return {Object} Ajax response object.
         */
        deleteNote: function(store, id) {
            return ajax(store, "delete", "/notes/" + id, { id: id }, "DELETE_NOTE", false, {
                type: "success",
                message: i18n.t("store.message.delete-note-success"),
                onlyCurrentRoute: true
            }, {
                type: "error",
                autoTranslate: true,
                onlyCurrentRoute: true
            });
        }
    },
    mutations: {
        // Create note
        API_CREATE_NOTE_PENDING: function() {},
        API_CREATE_NOTE_FAILURE: function() {},
        API_CREATE_NOTE_SUCCESS: function(state, data) {
            state.notes.unshift(data);
            state.selectedNote = data;
            state.lastSelectedUpdate = null;
        },
        // Edit note
        API_EDIT_NOTE_PENDING: function() {},
        API_EDIT_NOTE_FAILURE: function() {},
        API_EDIT_NOTE_SUCCESS: function(state, data) {
            // Instead, we remove and insert it at top (feels better)
            for (let i = 0, l = state.notes.length; i < l; i++) {
                if (state.notes[i].id == data.id) {
                    state.notes.splice(i, 1);
                    state.notes.splice(0, 0, data);
                }
            }
            state.selectedNote = data;
            state.lastSelectedUpdate = null;
        },
        // Get last updated at
        API_LAST_NOTE_UPDATED_PENDING: function() {},
        API_LAST_NOTE_UPDATED_FAILURE: function() {},
        API_LAST_NOTE_UPDATED_SUCCESS: function(state, data) {
            state.lastSelectedUpdate = data.updated_at;
        },
        // Reload notes
        API_RELOAD_NOTES_PENDING: function() {},
        API_RELOAD_NOTES_FAILURE: function() {},
        API_RELOAD_NOTES_SUCCESS: function(state, data) {
            // Both note and suggestion needs to erase
            // this array...
            state.notes.splice(0, state.notes.length);
            if (data.type === "note") {
                for (let i = 0, l = data.content.length; i < l; i++) {
                    state.notes.push(data.content[i]);
                }
            }
        },
        // Load notes
        API_LOAD_NOTES_PENDING: function() {},
        API_LOAD_NOTES_FAILURE: function() {},
        API_LOAD_NOTES_SUCCESS: function(state, data) {
            if (data.type !== "note") {
                return;
            }
            for (let i = 0, l = data.content.length; i < l; i++) {
                let found = false;
                for (let a = 0, b = state.notes.length; a < b && found === false; a++) {
                    if (state.notes[a].id == data.content[i].id) {
                        found = true;
                    }
                }
                if (!found) {
                    // TODO: we need to sort properly and insert at right place this
                    state.notes.push(data.content[i]);
                }
            }
        },
        // Load single note
        API_LOAD_SINGLE_NOTE_PENDING: function() {},
        API_LOAD_SINGLE_NOTE_FAILURE: function() {},
        API_LOAD_SINGLE_NOTE_SUCCESS: function(state, data) {
            state.selectedNote = data;
            state.lastSelectedUpdate = null;
        },
        // Delete note
        API_DELETE_NOTE_PENDING: function() {},
        API_DELETE_NOTE_FAILURE: function() {},
        API_DELETE_NOTE_SUCCESS: function(state, data) {
            let i = state.notes.length;
            while (i--) {
                if (state.notes[i].id == data.id) {
                    state.notes.splice(i, 1);
                }
            }
        },

        /**
         * Add a tag.
         *
         * @param {State} state The Vuex state.
         * @param {String} tag The tag to add.
         */
        addTag: function(state, tag) {
            if (!Array.isArray(state.selectedNote.tags)) {
                state.selectedNote = { ...state.selectedNote, tags: [] };
            }
            state.selectedNote.tags.push(tag);
        },

        /**
         * Remove a tag.
         *
         * @param {State} state The Vuex state.
         * @param {String} tag The tag to remove.
         */
        removeTag: function(state, tag) {
            if (!Array.isArray(state.selectedNote.tags)) {
                return;
            }
            let i = state.selectedNote.tags.length;
            while (i--) {
                if (state.selectedNote.tags[i] == tag) {
                    state.selectedNote.tags.splice(i, 1);
                }
            }
        },

        /**
         * Erase currently selected note.
         *
         * @param {State} state The Vuex state.
         */
        eraseSelectedNote: function(state) {
            const allowed = ["title", "content"];
            for (const key in state.selectedNote) {
                if (state.selectedNote.hasOwnProperty(key) && allowed.indexOf(key) === -1) {
                    Vue.delete(state.selectedNote, key);
                } else if (key === "title") {
                    state.selectedNote.title = "_(no title)_";
                } else if (key === "content") {
                    state.selectedNote.content = "";
                }
            }
        },

        /**
         * Set pending state.
         *
         * @param {Object} state The store's state.
         * @param {String} payload The name and value to set.
         */
        setPending: function(state, payload) {
            state.pending[payload.name] = payload.value;
        }
    }
};

export default store;